import React, { useEffect, useState } from "react";
import { Gavel, Send, Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";
import api from "../../api";
import { qaDetailText } from "../../utils/qaError";
import { useLang } from "../../context/LanguageContext";
import { useToast } from "../../hooks/use-toast";

/** Lets a shop reply once to a removal, watch or rejection — resolved in-app by the admin. */
export const AppealsCard = () => {
  const { t } = useLang();
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [openFor, setOpenFor] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/my/enforcement").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  if (!data) return null;

  const items = [];
  if (data.suspension) items.push({ type: "suspension", targetId: "", label: t("apl.suspension"), detail: data.suspension.reason });
  if (data.trust === "watch") items.push({ type: "trust", targetId: "", label: t("apl.watch"), detail: t("apl.watchDetail") });
  if (data.verificationRejected) items.push({ type: "verification", targetId: data.verificationRejected.id, label: t("apl.verification"), detail: data.verificationRejected.reason });
  (data.removedDeals || []).forEach((d) => items.push({ type: "deal", targetId: d.id, label: t("apl.deal", { title: d.title }), detail: d.hiddenReason }));
  if (!items.length) return null;

  const appealFor = (it) => (data.appeals || []).find((a) => a.type === it.type && (a.targetId || "") === (it.targetId || ""));

  const submit = async (it) => {
    setBusy(true);
    try {
      await api.post("/my/appeals", { type: it.type, targetId: it.targetId, message });
      toast({ title: t("apl.sent"), description: t("apl.sentMsg") });
      setOpenFor(null); setMessage("");
      await load();
    } catch (e) {
      toast({ title: t("apl.failed"), description: qaDetailText(t, e.response?.data?.detail) });
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="appeals-card">
      <h2 className="font-bold text-foreground flex items-center gap-2"><Gavel size={18} className="text-amber-600" /> {t("apl.title")}</h2>
      <p className="text-xs text-muted-foreground mt-0.5 mb-3">{t("apl.subtitle")}</p>

      <div className="space-y-2">
        {items.map((it) => {
          const ap = appealFor(it);
          const key = `${it.type}:${it.targetId}`;
          return (
            <div key={key} className="border border-border rounded-xl p-3" data-testid={`appeal-item-${key}`}>
              <p className="text-sm font-bold text-foreground">{it.label}</p>
              {it.detail && <p className="text-xs text-muted-foreground mt-0.5">{t("apl.reason")}: {it.detail}</p>}

              {ap ? (
                <p className={`text-xs font-semibold mt-2 flex items-center gap-1.5 ${ap.status === "open" ? "text-amber-700" : ap.outcome === "granted" ? "text-[#16a34a]" : "text-rose-600"}`}
                  data-testid={`appeal-status-${key}`}>
                  {ap.status === "open" ? <><Clock size={13} /> {t("apl.pending")}</>
                    : ap.outcome === "granted" ? <><CheckCircle2 size={13} /> {t("apl.granted")}</>
                      : <><XCircle size={13} /> {ap.adminNote || t("apl.declined")}</>}
                </p>
              ) : openFor === key ? (
                <div className="mt-2">
                  <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
                    data-testid={`appeal-message-${key}`} placeholder={t("apl.placeholder")}
                    className="w-full bg-secondary rounded-xl px-3 py-2 text-sm outline-none resize-none" />
                  <div className="flex items-center gap-2 mt-2">
                    <button onClick={() => submit(it)} disabled={busy} data-testid={`appeal-submit-${key}`}
                      className="inline-flex items-center gap-1.5 text-xs font-bold bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white rounded-xl px-3 py-2 transition-colors">
                      {busy ? <Loader2 className="animate-spin" size={13} /> : <Send size={13} />} {t("apl.send")}
                    </button>
                    <button onClick={() => { setOpenFor(null); setMessage(""); }}
                      className="text-xs font-semibold border border-border hover:bg-secondary rounded-xl px-3 py-2">{t("apl.cancel")}</button>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1.5">{t("apl.onceNote")}</p>
                </div>
              ) : (
                <button onClick={() => { setOpenFor(key); setMessage(""); }} data-testid={`appeal-open-${key}`}
                  className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold border border-border hover:border-[#16a34a] rounded-xl px-3 py-2 transition-colors">
                  <Gavel size={13} /> {t("apl.appeal")}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AppealsCard;
